import { z } from "zod";

export const identitySchema = z.object({
  subject: z.string(),
  email: z.email(),
  name: z.string(),
  admin: z.boolean(),
  expires: z.iso.datetime(),
});
export const serverSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,62}$/),
  alias: z.string().regex(/^[a-z][a-z0-9-]{0,62}$/),
  address: z.ipv4(),
  port: z.number().int().min(1).max(65535),
  login: z
    .string()
    .regex(/^[a-z_][a-z0-9_-]{0,31}$/)
    .refine((v) => v !== "root", "Use a non-root Linux account"),
  peer_id: z.string().min(1),
  provision_user: z.string().min(1),
  host_key: z.string().regex(/^(ssh-|ecdsa-|sk-)/, "Paste an OpenSSH public host key"),
  enabled: z.boolean(),
});
export const ruleSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,62}$/),
  server_id: z.string().min(1),
  group_id: z.string().min(1),
  group_name: z.string().startsWith("SSH-"),
  enabled: z.boolean(),
});
export const stateSchema = z.object({
  servers: z.array(serverSchema),
  rules: z.array(ruleSchema),
  deployments: z.array(
    z.object({
      server_id: z.string(),
      desired: z.array(z.string()),
      applied: z.array(z.string()),
      status: z.string(),
      error: z.string(),
      updated_at: z.iso.datetime(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.number(),
      actor: z.string(),
      action: z.string(),
      target: z.string(),
      detail: z.string(),
      at: z.iso.datetime(),
    }),
  ),
  last_sync: z.iso.datetime().nullable(),
  sync_error: z.string(),
});
export const inventorySchema = z.object({
  groups: z.array(z.object({ id: z.string(), name: z.string() })),
  peers: z.array(z.object({ id: z.string(), ip: z.string(), user_id: z.string() })),
});
export const aliasesSchema = z.array(
  z.object({
    alias: z.string(),
    address: z.string(),
    port: z.number(),
    login: z.string(),
    host_key: z.string(),
  }),
);
export type Server = z.infer<typeof serverSchema>;
export type Rule = z.infer<typeof ruleSchema>;
export type AccessState = z.infer<typeof stateSchema>;

export const serverFormSchema = serverSchema.omit({ enabled: true }).extend({
  port: z
    .string()
    .regex(/^\d+$/)
    .refine((value) => Number(value) >= 1 && Number(value) <= 65535, "Use a port from 1 to 65535"),
});

export const consentSchema = z.object({ code: z.string(), email: z.email(), csrf: z.string() });
export const approvalSchema = z.object({ approved: z.boolean() });

export const apiErrorSchema = z.object({ error: z.string() });
